import {Component, Inject} from "@angular/core";
import {cloneDeep, forOwn} from "lodash";
import {
    AcDialog,
    DIALOG_CONFIG,
    DialogConfig,
    SchemaHelperService,
    ValidatorsService
} from "ac-infra";
import {OperatorsRestService} from "../../../system/administration/administration-api/operators-rest.service";
import {AuthGroup} from "../../../common/utilities/session-helper.service";
import {SessionService} from "../../../common/services/session.service";
import {AuthorizationService} from "../../../common/services/authorization.service";
import {AlarmsActionsService} from "../../services/alarms-actions.service";
import {ServerInfoService} from "../../../common/services/server-info.service";

@AcDialog({
    id: "forwarding-rule-dialog",
    cancelButtonText: "Close",
    width: 650,
    height: 750,
})
@Component({
    selector: "alarms-forwarding-dialog",
    templateUrl: "./alarms-forwarding-dialog.component.html",
    styleUrls: ["./alarms-forwarding-dialog.component.less"]
})
export class AlarmsForwardingDialogComponent {

    alarmsFWDAddSchema = require("../../../../schema/post/alarmsfwdruleprocessor.json");
    alarmsFWDEditSchema = require("../../../../schema/put/alarmsfwdruleprocessor.json");
    alarmsFWDEmpty = require("./alarmsfwd.empty.json");
    journalsFWDEmpty = require("./journalsfwd.empty.json");

    emptyRule;
    additionalSettings;
    isEdit;
    viewOnly;
    tenantsOptions;
    session;
    allowNullProperties = ["alarmTimeSlotsFilter.sunday", "alarmTimeSlotsFilter.monday", "alarmTimeSlotsFilter.tuesday", "alarmTimeSlotsFilter.wednesday", "alarmTimeSlotsFilter.thursday",
        "alarmTimeSlotsFilter.friday", "alarmTimeSlotsFilter.saturday",
        "actionNamesFilter", "alarmGroupsFilter", "alarmDevicesFilter", "alarmSitesFilter", "alarmEndpointsFilter", "alarmSourceFilter",
        "alarmRegionsFilter", "alarmTenantsFilter", "alarmCustomersFilter", "alarmExternalAppsFilter", "alarmLinksFilter", "eventOriginFilter", "alarmOriginFilter", "alarmNamesFilter", "alarmSeverityFilter", "alarmTypesFilter", "destNotification.operators"];
    forwardingType = {
        options: [
            {value: "ALLOW", text: "Forward Alarms matching Topology and Rule conditions"},
            {value: "DENY", text: "Prevent Forwarding of Alarms matching Topology and Rule conditions"}
        ]
    };
    ALARM_EVENT_TYPE = AlarmsActionsService.ALARM_EVENT_TYPE;
    externalApps;
    public rule: any = {};
    public schema: any;
    public tenantOperatorsOptions: any;

    constructor(private sessionService: SessionService,
                private operatorsRestService: OperatorsRestService,
                private authorizationService: AuthorizationService,
                @Inject(DIALOG_CONFIG) public dialogConfig: DialogConfig) {
    }

    ngOnInit() {
        this.session = this.sessionService.activeSession;
        const isOvocVersion842000 = ServerInfoService.serverInfo.ovocVersion.startsWith('8.4.2');


        this.rule = this.dialogConfig.dialogData.entity;
        this.externalApps = this.dialogConfig.dialogData.externalApps;
        this.additionalSettings = cloneDeep(this.rule);
        this.emptyRule = cloneDeep(this.additionalSettings.ruleType === AlarmsActionsService.ALARM_EVENT_TYPE ? this.alarmsFWDEmpty : this.journalsFWDEmpty);

        this.dialogConfig.title = this.additionalSettings.ruleType === AlarmsActionsService.ALARM_EVENT_TYPE ? "Alarms Forwarding Rule Details" : "Journal Forwarding Rule Details";
        this.isEdit = this.dialogConfig.dialogData.isEdit;

        if(isOvocVersion842000){
            if(!this.isEdit){
                this.rule.alarmExternalAppsFilter = this.authorizationService.isSystemAdminOrOperatorLevel() ? null : [];
            }
        }

        this.viewOnly = !!(this.isEdit && this.authorizationService.validFor(AuthGroup.SYSTEM_MONITORING));


        if (this.additionalSettings.ruleType === AlarmsActionsService.ALARM_EVENT_TYPE) {
            this.tenantsOptions = this.dialogConfig.dialogData.tenants;
            this.dialogConfig.preSubmit = this.viewOnly ? null : () => this.preSubmit();
        }

        this.schema = cloneDeep(this.isEdit ? this.alarmsFWDEditSchema : this.alarmsFWDAddSchema);
    }

    preSubmit = () => {
        if (this.rule.destSnmp && this.rule.destSnmp.snmpVersion === "SNMP_VERSION_3" && !this.rule.destSnmp.snmpV3UserProfileInfo) {
            delete this.rule.destSnmp.snmpVersion;
        }
    };
    validateAuxiliaryForm = (errors, auxModel, formModel) => {
        for (let i = errors.length - 1; i >= 0; i--) {
            if (errors[i].instancePath === "/alarmSourceFilter" && errors[i].keyword === "type") {
                errors.splice(i, 1);
            }
        }

        if (this.additionalSettings.ruleDestType === "SNMP" && this.rule.destSnmp.snmpIp !== undefined && !SchemaHelperService.validateIPAddress(this.rule.destSnmp.snmpIp, "both")) {
            const error = SchemaHelperService.buildErrorItem({inputName: "rule.destSnmp.snmpIp", keyword: "IPv4or6Aux"});
            errors.unshift(error);
        }
        if (this.additionalSettings.ruleDestType === "SYSLOG" && this.rule.destSyslog.syslogServerIP !== undefined && !SchemaHelperService.validateIPAddress(this.rule.destSyslog.syslogServerIP, "both")) {
            const error = SchemaHelperService.buildErrorItem({inputName: "rule.destSyslog.syslogServerIP", keyword: "IPv4or6Aux"});
            errors.unshift(error);
        }
    };

    requiredsAuxiliaryForm = () => {
        const requiredList = [];
        if (this.additionalSettings.ruleDestType === "MAIL") {
            requiredList.push("rule.destMail.mailTo");

            if (!this.rule.destMail.useInternalMailServer) {
                requiredList.push("rule.destMail.mailHost");
                requiredList.push("rule.destMail.mailFrom");
            }
        }

        return requiredList;
    };

    getOperatorsFromServerAndUpdateNotificationItems = () => {
        const authorizedForSystemAdmin = this.authorizationService.validFor(AuthGroup.SYSTEM_ADMIN);
        const isTenantAdmin = this.authorizationService.validFor(AuthGroup.TENANT_ADMIN);
        const isSystemMonitor = this.authorizationService.validFor(AuthGroup.SYSTEM_MONITORING);
        const isOperatorLevel = this.authorizationService.validFor(AuthGroup.operator);
        const isLDAPOrRadiusOrAzureUser = this.session.operatorAuthenticationMode !== "LOCAL_AUTH";

        this.tenantOperatorsOptions = [];
        if (!this.rule.destNotification) {
            this.rule.destNotification = {operators: null};
        }

        forOwn(this.rule.destNotification.operators, (operator) => {
            this.tenantOperatorsOptions.push({text: operator, value: operator});
        });

        if (isOperatorLevel || isLDAPOrRadiusOrAzureUser) {
            this.tenantOperatorsOptions.push({text: this.session.name, value: this.session.name});
        }

        if (isSystemMonitor || isOperatorLevel || isLDAPOrRadiusOrAzureUser) {
            return;
        }

        this.operatorsRestService.getOperators((response) => {
            let operatorsList;
            const operators = response.data.operators;
            const currentOperator = operators.find((operator) => operator.name === this.session.name);

            if (authorizedForSystemAdmin) {
                if (this.additionalSettings.tenantId === -1 || this.rule.tenantId === -1) {// all tenants
                    operatorsList = operators;
                } else {// specific tenant
                    operatorsList = operators.filter((operator) => operator.assignedTenants && operator.assignedTenants.includes(this.additionalSettings.tenantId || this.rule.tenantId));
                }
            } else if (isTenantAdmin) {
                operatorsList = operators.filter((operator) => currentOperator.assignedTenants.some((assignedTenant) => operator.assignedTenants.includes(assignedTenant)));
            } else {
                operatorsList = [currentOperator];
            }

            this.tenantOperatorsOptions = operatorsList.map((item) => ({value: item.name, text: item.name}));
        });

        if (this.rule.destNotification && this.rule.destNotification.operators !== null && this.rule.destNotification.operators.length > this.tenantOperatorsOptions.length) {
            forOwn(this.rule.destNotification.operators, (operator) => {
                const operatorObj = {value: operator, text: operator};

                if (!this.tenantOperatorsOptions.includes(operatorObj)) {
                    this.tenantOperatorsOptions.push(operatorObj);
                }
            });
        }
    };

    ruleForwardTypeChanged = () => {
        this.additionalSettings.dontSendAlarms = false;
        this.additionalSettings.dontSendEvents = false;
    };
}


